import { CONFIG } from "../../config";
import { getTokenizedAxios } from "../../utils/axiosWithTokenGen";

/**
 * klearlyMerchantInsights owns the `/insights` subtree on the **main app** REST
 * API, not admin's own — so this uses SERVER, like klearlyMarketingService.
 * The route is protected by the admin Cognito pool, which is the token KBM
 * already sends. Pointing it at ADMIN_SERVER returns `Missing Authentication
 * Token`, which a browser reports as a CORS failure.
 */
export interface ResyncMerchantInsightsResponse {
  businessId: string;
  lookbackDays: number;
  executionArn: string;
  dailyAggregatesRequested: boolean;
}

export async function resyncMerchantInsights(
  businessId: string
): Promise<ResyncMerchantInsightsResponse> {
  const axios = await getTokenizedAxios();

  return axios
    .post(
      `${CONFIG.SERVER.BASE_URL}/insights/resync?businessId=${businessId}`,
      {}
    )
    .then((res) => res?.data);
}

export type ForecastModelState =
  "learning" | "noModelYet" | "modelUnusable" | "belowQualityBar" | "ready";

export interface ForecastModelStatusResponse {
  businessId: string;
  state: ForecastModelState;
  isShownToMerchant: boolean;
  /** What to do about this state, written by the server. */
  nextAction: string;
  trainingDays: number;
  /** Null on a stage that enforces no floor. */
  minTrainingDays: number | null;
  validationScore: number;
  minValidationScore: number | null;
  lastTrainedAt: string | null;
  /** First day in the training window, when the server sends it. */
  dataFrom?: string | null;
  dataThrough: string | null;
  featureCount: number;
  tagFeatures: string[];
  resyncRequestedAt: string | null;
  hasSnapshot: boolean;
  hasModelPointer: boolean;
  /** False with a pointer present means the artifact itself is the problem. */
  isModelLoadable: boolean;
  modelUri: string | null;
}

export async function getForecastModelStatus(
  businessId: string
): Promise<ForecastModelStatusResponse> {
  const axios = await getTokenizedAxios();

  return axios
    .get(
      `${CONFIG.SERVER.BASE_URL}/insights/model-status?businessId=${businessId}`
    )
    .then((res) => res?.data);
}
